import { useCallback, useEffect, useState } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * CountyRegistryModal — Zero-Dollar Property Ingestion registry admin.
 *
 * County ArcGIS FeatureServer endpoints rotate quarterly. This surface
 * lets the CEO swap the URL / address field / field map at runtime —
 * no deploy required. Overrides land in `db.county_gis_overrides` and
 * are merged on top of the code defaults on the next property auto-fetch.
 */
const CountyRegistryModal = ({ open, onClose }) => {
  const [counties, setCounties] = useState([]);
  const [loading, setLoading]   = useState(false);
  const [editing, setEditing]   = useState(null);   // county object or null
  const [err, setErr]           = useState("");

  const load = useCallback(async () => {
    setLoading(true); setErr("");
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/property-registry`, { credentials: "include" });
      if (r.ok) {
        const d = await r.json();
        setCounties(d.counties || []);
      } else {
        setErr(`Could not load registry (HTTP ${r.status})`);
      }
    } catch { setErr("Network error loading registry."); }
    setLoading(false);
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  if (!open) return null;

  return (
    <div data-testid="county-registry-backdrop" onClick={onClose}
         style={{ position: "fixed", inset: 0, background: "rgba(2,8,20,0.78)",
                  display: "flex", justifyContent: "center", alignItems: "flex-start",
                  padding: "3rem 1.5rem", zIndex: 9998, overflowY: "auto" }}>
      <div onClick={(e) => e.stopPropagation()} data-testid="county-registry-modal"
           style={{ background: "linear-gradient(160deg, #0a1929 0%, #1b2a4e 100%)",
                    border: "1px solid rgba(184,145,90,0.5)", borderRadius: 10,
                    maxWidth: 1050, width: "100%", padding: "1.8rem 2rem 1.6rem",
                    color: "#F5EFE1", boxShadow: "0 40px 80px rgba(0,0,0,0.5)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start",
                      marginBottom: "1.1rem", flexWrap: "wrap", gap: "0.7rem" }}>
          <div>
            <div style={{ color: "#B8915A", fontSize: "0.7rem", letterSpacing: "0.22em",
                          textTransform: "uppercase", fontWeight: 800 }}>
              Zero-Dollar Property Registry
            </div>
            <h2 style={{ margin: "0.3rem 0 0", fontSize: "1.4rem", fontWeight: 700 }}>
              County GIS FeatureServer Registry
            </h2>
            <div style={{ color: "#ADBED1", fontSize: "0.85rem", marginTop: 4 }}>
              Swap a county's ArcGIS endpoint at runtime. Overrides merge on top of code defaults — no deploy required.
            </div>
          </div>
          <button type="button" data-testid="county-registry-close" onClick={onClose}
                  style={{ background: "transparent", color: "#B8915A", border: "1px solid #B8915A",
                           padding: "0.45rem 0.9rem", borderRadius: 4, fontSize: "0.8rem", fontWeight: 700, cursor: "pointer" }}>
            Close ✕
          </button>
        </div>

        {err && <div data-testid="cr-error" style={{ background: "rgba(220,38,38,0.14)", border: "1px solid rgba(220,38,38,0.4)",
                                                     color: "#FCA5A5", padding: "0.6rem 0.9rem", borderRadius: 5, marginBottom: "0.9rem" }}>⚠ {err}</div>}

        {loading && <div style={{ color: "#ADBED1", padding: "1rem 0" }}>Loading registry…</div>}

        {!loading && !editing && (
          <>
            <div style={{ display: "grid", gap: "0.55rem", maxHeight: "55vh", overflowY: "auto" }}>
              {counties.map((c) => (
                <div key={c.key} data-testid={`cr-row-${c.key}`}
                     style={{ background: "rgba(0,0,0,0.22)", padding: "0.8rem 0.95rem", borderRadius: 5,
                              borderLeft: `3px solid ${c.source === "override" ? "#5AC8D8" : c.source === "runtime_added" ? "#00DC78" : "#B8915A"}` }}>
                  <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: "0.5rem" }}>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ color: "#F3F4F6", fontSize: "0.95rem", fontWeight: 700 }}>
                        {c.label} <span style={{ color: "#B8915A", fontSize: "0.72rem", marginLeft: 6, fontWeight: 700 }}>· {c.key}</span>
                        <span style={{ marginLeft: 8, fontSize: "0.65rem", color: c.source === "code_default" ? "#B8915A" : "#5AC8D8",
                                       fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.12em" }}>
                          {c.source === "code_default" ? "Default" : c.source === "override" ? "Override" : "Runtime-Added"}
                        </span>
                      </div>
                      <div style={{ color: "#ADBED1", fontSize: "0.75rem", marginTop: 3, wordBreak: "break-all" }}>
                        {c.feature_url}
                      </div>
                      <div style={{ color: "#7E92A8", fontSize: "0.7rem", marginTop: 2 }}>
                        SITUS field: <code>{c.address_field}</code> · {Object.keys(c.field_map || {}).length} mapped attributes
                      </div>
                    </div>
                    <button type="button" data-testid={`cr-edit-${c.key}`} onClick={() => setEditing(c)}
                            style={{ background: "transparent", color: "#5AC8D8", border: "1px solid #5AC8D8",
                                     padding: "0.4rem 0.85rem", borderRadius: 4, fontSize: "0.75rem", fontWeight: 700, cursor: "pointer" }}>
                      ✎ Edit
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <div style={{ marginTop: "1rem", textAlign: "right" }}>
              <button type="button" data-testid="cr-add-btn"
                      onClick={() => setEditing({ key: "", label: "", county: "", feature_url: "", address_field: "SITUS_ADDRESS", field_map: { square_footage: "", bedrooms: "", bathrooms: "", year_built: "" } })}
                      style={{ background: "linear-gradient(90deg, #B8915A, #8B6B3F)", color: "#F5EFE1",
                               border: "none", padding: "0.55rem 1.2rem", borderRadius: 5,
                               fontSize: "0.85rem", fontWeight: 800, cursor: "pointer" }}>
                + Add County
              </button>
            </div>
          </>
        )}

        {editing && (
          <CountyEditor
            initial={editing}
            onCancel={() => setEditing(null)}
            onSaved={() => { setEditing(null); load(); }}
          />
        )}
      </div>
    </div>
  );
};


const CountyEditor = ({ initial, onCancel, onSaved }) => {
  const [form, setForm] = useState({
    key:           initial.key || "",
    label:         initial.label || "",
    county:        initial.county || "",
    feature_url:   initial.feature_url || "",
    address_field: initial.address_field || "SITUS_ADDRESS",
    field_map:     initial.field_map || {},
    notes:         "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr]   = useState("");

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setMap = (k, v) => setForm((f) => ({ ...f, field_map: { ...f.field_map, [k]: v } }));

  const save = async () => {
    if (!form.key.trim() || !form.feature_url.trim()) {
      setErr("Key and FeatureServer URL are required."); return;
    }
    setBusy(true); setErr("");
    try {
      // Strip blank entries from field_map
      const cleanMap = Object.fromEntries(Object.entries(form.field_map).filter(([, v]) => v && v.trim()));
      const r = await fetch(`${BACKEND_URL}/api/admin/property-registry`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, field_map: cleanMap }),
      });
      if (r.ok) onSaved();
      else {
        const d = await r.json().catch(() => ({}));
        setErr(d.detail || `HTTP ${r.status}`);
      }
    } catch { setErr("Network error saving override."); }
    setBusy(false);
  };

  const input = (k, ph) => (
    <input data-testid={`cr-edit-${k}`} value={form[k]}
           onChange={(e) => setF(k, e.target.value)} placeholder={ph}
           style={{ width: "100%", background: "rgba(0,0,0,0.28)", color: "#F5EFE1",
                    border: "1px solid rgba(184,145,90,0.3)", borderRadius: 4,
                    padding: "0.5rem 0.7rem", fontSize: "0.85rem" }} />
  );
  const mapInput = (k, ph) => (
    <input data-testid={`cr-map-${k}`} value={form.field_map[k] || ""}
           onChange={(e) => setMap(k, e.target.value)} placeholder={ph}
           style={{ width: "100%", background: "rgba(0,0,0,0.28)", color: "#F5EFE1",
                    border: "1px solid rgba(90,200,216,0.3)", borderRadius: 4,
                    padding: "0.45rem 0.65rem", fontSize: "0.82rem" }} />
  );
  const label = (t) => <div style={{ color: "#B8915A", fontSize: "0.7rem", fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: 3 }}>{t}</div>;

  return (
    <div data-testid="cr-editor" style={{ display: "grid", gap: "0.75rem", maxHeight: "62vh", overflowY: "auto", paddingRight: 4 }}>
      {err && <div style={{ background: "rgba(220,38,38,0.14)", border: "1px solid rgba(220,38,38,0.4)", color: "#FCA5A5", padding: "0.6rem 0.9rem", borderRadius: 5 }}>⚠ {err}</div>}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: "0.6rem" }}>
        <div>{label("Key *")}{input("key", "DCAD")}</div>
        <div>{label("Label")}{input("label", "Dallas Central Appraisal District")}</div>
      </div>
      <div>{label("County")}{input("county", "Dallas")}</div>
      <div>{label("FeatureServer URL *")}{input("feature_url", "https://gis.example.gov/arcgis/rest/services/.../MapServer/0")}</div>
      <div>{label("SITUS Address Field")}{input("address_field", "SITUS_ADDRESS")}</div>

      <div style={{ background: "rgba(90,200,216,0.05)", border: "1px solid rgba(90,200,216,0.2)", borderRadius: 6, padding: "0.7rem 0.9rem" }}>
        <div style={{ color: "#5AC8D8", fontSize: "0.72rem", fontWeight: 800, letterSpacing: "0.15em", textTransform: "uppercase", marginBottom: "0.5rem" }}>
          ArcGIS Attribute Map
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.5rem 0.8rem" }}>
          <div>{label("Living Area")}{mapInput("square_footage", "TOT_LVG_AREA_SF")}</div>
          <div>{label("Bedrooms")}{mapInput("bedrooms", "NUM_BEDROOMS")}</div>
          <div>{label("Bathrooms")}{mapInput("bathrooms", "NUM_FULL_BATHS")}</div>
          <div>{label("Year Built")}{mapInput("year_built", "ACT_YEAR_BUILT")}</div>
          <div>{label("Property Class")}{mapInput("property_class", "PROPERTY_CLASS")}</div>
          <div>{label("Appraised Value")}{mapInput("appraised_value_usd", "TOT_VAL")}</div>
          <div style={{ gridColumn: "1 / -1" }}>{label("Parcel ID")}{mapInput("parcel_id", "ACCOUNT_NUM")}</div>
        </div>
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", gap: "0.5rem", marginTop: "0.5rem" }}>
        <button type="button" data-testid="cr-editor-cancel" onClick={onCancel}
                style={{ background: "transparent", color: "#ADBED1", border: "1px solid rgba(255,255,255,0.2)",
                         padding: "0.55rem 1.2rem", borderRadius: 5, fontSize: "0.85rem", fontWeight: 700, cursor: "pointer" }}>
          Cancel
        </button>
        <button type="button" data-testid="cr-editor-save" onClick={save} disabled={busy}
                style={{ background: "linear-gradient(90deg, #5AC8D8, #00DC78)", color: "#062617",
                         border: "none", padding: "0.55rem 1.4rem", borderRadius: 5,
                         fontSize: "0.85rem", fontWeight: 800, cursor: busy ? "not-allowed" : "pointer", opacity: busy ? 0.6 : 1 }}>
          {busy ? "Saving…" : "💾 Save Override"}
        </button>
      </div>
    </div>
  );
};


export default CountyRegistryModal;
